import { ApiError } from './api.utils';
import type { ApiResponseError } from './api.utils';

export interface ApiClientConfig {
  /**
   * Base URL for all API requests. Should NOT include a version segment;
   * call sites are expected to write fully versioned paths (e.g. `/v1/...`,
   * `/v3/...`).
   * @example 'https://api.example.com'
   * @example '/api'
   */
  baseUrl: string;

  /**
   * Optional custom error handler. Called when a request fails (non-ok response).
   * If provided, this handler is responsible for throwing an error.
   * If not provided, a default ApiError is thrown.
   * @param response - The failed Response object
   * @param responseBody - The response body as text
   * @param request - Details about the request that failed
   * @param request.method - The HTTP method of the failed request
   * @param request.url - The URL of the failed request
   */
  onError?: (
    response: Response,
    responseBody: string,
    request: { method: string; url: string }
  ) => never;
}

export type RequestOptions = Omit<RequestInit, 'method' | 'body'>;

let baseUrl: string;
let onError: ApiClientConfig['onError'];

export const initializeApi = (config: ApiClientConfig) => {
  baseUrl = config.baseUrl;
  onError = config.onError;
};

const buildUrl = (path: string): string => {
  if (!baseUrl) {
    throw new Error('Api not initialized');
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

const headersInitToRecord = (
  initialHeaders: HeadersInit
): Record<string, string> => {
  if (initialHeaders instanceof Headers) {
    return Object.fromEntries(initialHeaders.entries());
  }
  if (Array.isArray(initialHeaders)) {
    return Object.fromEntries(initialHeaders);
  }
  return initialHeaders;
};

const buildHeaders = (
  token?: string | null,
  options?: RequestInit
): Headers => {
  const headers =
    options?.headers === undefined
      ? {}
      : headersInitToRecord(options.headers as HeadersInit);

  return new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token
      ? { Authorization: token.includes('Bearer') ? token : `Bearer ${token}` }
      : {}),
    ...headers,
  });
};

const handleResponse = async <T>(
  response: Response,
  request: { method: string; url: string }
): Promise<T | null> => {
  if (!response.ok) {
    const responseBody = await response.text();

    if (onError) {
      onError(response, responseBody, request);
    } else {
      let errorMessage = 'API request failed';
      let structuredErrors: ApiResponseError[] = [];
      if (responseBody) {
        try {
          const parsed = JSON.parse(responseBody);
          if (Array.isArray(parsed.errors) && parsed.errors.length) {
            structuredErrors = parsed.errors as ApiResponseError[];
            const details = structuredErrors
              .map((e) => e.detail)
              .filter(Boolean) as string[];
            errorMessage = details.join('. ') || errorMessage;
          } else {
            errorMessage =
              parsed.error ||
              parsed.errors?.error ||
              parsed.detail ||
              parsed.message ||
              errorMessage;
          }
        } catch {
          // Not JSON, use default message
        }
      }
      throw new ApiError(errorMessage, response, structuredErrors);
    }
  }

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();

  if (text === '' || text.toLowerCase() === 'ok') {
    return { ok: true } as T;
  }

  if (text.startsWith('{') || text.startsWith('[')) {
    return JSON.parse(text) as T;
  }

  return text as unknown as T;
};

export const api = {
  get: async <T>(
    path: string,
    token?: string | null,
    options?: RequestOptions
  ): Promise<T> => {
    const url = buildUrl(path);
    const response = await fetch(url, {
      ...options,
      headers: buildHeaders(token, options),
      method: 'GET',
    });
    return handleResponse(response, { method: 'GET', url }) as Promise<T>;
  },

  post: async <T>(
    path: string,
    data: unknown,
    token?: string | null,
    options?: RequestOptions
  ): Promise<T> => {
    const url = buildUrl(path);
    const response = await fetch(url, {
      ...options,
      body: JSON.stringify(data),
      headers: buildHeaders(token, options),
      method: 'POST',
    });
    return handleResponse(response, { method: 'POST', url }) as Promise<T>;
  },

  patch: async <T>(
    path: string,
    data: unknown,
    token?: string | null,
    options?: RequestOptions
  ): Promise<T> => {
    const url = buildUrl(path);
    const response = await fetch(url, {
      ...options,
      body: JSON.stringify(data),
      headers: buildHeaders(token, options),
      method: 'PATCH',
    });
    return handleResponse(response, {
      method: 'PATCH',
      url,
    }) as Promise<T>;
  },

  put: async <T>(
    path: string,
    data: unknown,
    token?: string | null,
    options?: RequestOptions
  ): Promise<T> => {
    const url = buildUrl(path);
    const response = await fetch(url, {
      ...options,
      body: JSON.stringify(data),
      headers: buildHeaders(token, options),
      method: 'PUT',
    });
    return handleResponse(response, { method: 'PUT', url }) as Promise<T>;
  },

  delete: async <T>(
    path: string,
    token?: string | null,
    options?: RequestOptions
  ): Promise<T | null> => {
    const url = buildUrl(path);
    const response = await fetch(url, {
      ...options,
      headers: buildHeaders(token, options),
      method: 'DELETE',
    });
    return handleResponse(response, { method: 'DELETE', url });
  },
};
